import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';

const Home = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-navy-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" 
            alt="Corporate strategy meeting" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/90 to-transparent"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-32 pb-40">
          <motion.div 
            className="max-w-3xl"
            initial="initial"
            animate="animate"
            variants={fadeIn}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-navy-800 border border-navy-700 text-gold-500 text-sm font-medium mb-6">
              <SafeIcon name="Award" className="w-4 h-4" /> 
              <span>World-Class Systems for UK Health And Social Care</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-serif font-bold leading-tight mb-6">
              Your Strategic Partner for <span className="text-gold-500">CQC 'Outstanding'</span> and Operational Excellence.
            </h1>
            
            <p className="text-xl text-slate-300 mb-10 leading-relaxed">
              I help health and social care leaders build resilient organisations, develop high-performing teams, and deliver world-class care through proven, scalable systems.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/services" 
                className="bg-gold-500 text-navy-900 px-8 py-4 rounded font-semibold text-center hover:bg-gold-400 transition-colors shadow-lg shadow-gold-500/20"
              >
                Explore Our Systems
              </Link>
              <Link 
                to="/contact" 
                className="bg-transparent border-2 border-slate-600 text-white px-8 py-4 rounded font-semibold text-center hover:border-white transition-colors"
              >
                Book a Strategic Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-serif font-bold text-navy-900 mb-6">Navigating the Complexities of Modern Care.</h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Senior leaders in the care sector face immense pressure: CQC inspections, local authority contracts, and narrowing margins. Quick fixes don't work in a high-stakes environment.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                The Kajidori Collective provides <strong className="text-navy-900 font-semibold">Strategic Leadership, Operational Clarity, and World-Class Excellence</strong>. We build robust, sustainable systems that empower frontline teams, equip middle management, and give senior leaders the ROI and peace of mind they demand.
              </p>
              <Link to="/about" className="inline-flex items-center gap-2 text-navy-900 font-semibold hover:text-gold-600 transition-colors">
                Meet Yeukai Kajidori <SafeIcon name="ArrowRight" />
              </Link>
            </motion.div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gold-500 rounded-lg transform translate-x-4 translate-y-4"></div>
              <img 
                src="https://media-manager-c.questera.ai/greta-media/ba94756da441093814cfe3ceab57db9a070deed6a3cd4fa7be0058ed1320fa8aecce39965b827539d337d47996686592/images/aW1hZ2UvanBlZw==/24969d653a4f9687f41bb940bf68c8ba.jpeg" 
                alt="Yeukai Kajidori" 
                className="relative z-10 rounded-lg shadow-xl w-full object-cover object-top h-[500px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-serif font-bold text-navy-900 mb-4">The Three Pillars of Success</h2>
            <p className="text-lg text-slate-600">Comprehensive solutions designed for every level of your organisation.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-50 border border-slate-100 p-10 rounded-xl hover:shadow-xl transition-shadow group">
              <div className="w-14 h-14 bg-navy-50 rounded-lg flex items-center justify-center mb-6 group-hover:bg-navy-900 transition-colors">
                <SafeIcon name="TrendingUp" className="w-7 h-7 text-navy-900 group-hover:text-gold-500 transition-colors" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-navy-900 mb-4">Strategic Consulting</h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                For Senior Leaders. Turnarounds, operational excellence, and robust QA systems to build an 'Outstanding' organisation.
              </p>
              <Link to="/services" className="text-sm font-bold text-navy-900 flex items-center gap-1 group-hover:text-gold-600 transition-colors">
                Learn More <SafeIcon name="ChevronRight" className="w-4 h-4" />
              </Link>
            </div>

            <div className="bg-navy-900 text-white border border-navy-800 p-10 rounded-xl shadow-2xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gold-500/10 rounded-full blur-2xl transform translate-x-10 -translate-y-10"></div>
              <div className="w-14 h-14 bg-white/10 rounded-lg flex items-center justify-center mb-6 backdrop-blur-sm">
                <SafeIcon name="BookOpen" className="w-7 h-7 text-gold-500" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-white mb-4">Flagship Training</h3>
              <p className="text-slate-300 mb-6 leading-relaxed">
                The Mental Health & Complex Needs Excellence Programme. Empowering frontline teams and middle management for ultimate compliance.
              </p>
              <Link to="/services" className="text-sm font-bold text-gold-500 flex items-center gap-1 hover:text-white transition-colors">
                View Programme <SafeIcon name="ChevronRight" className="w-4 h-4" />
              </Link>
            </div>

            <div className="bg-slate-50 border border-slate-100 p-10 rounded-xl hover:shadow-xl transition-shadow group">
              <div className="w-14 h-14 bg-navy-50 rounded-lg flex items-center justify-center mb-6 group-hover:bg-navy-900 transition-colors">
                <SafeIcon name="Users" className="w-7 h-7 text-navy-900 group-hover:text-gold-500 transition-colors" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-navy-900 mb-4">Leadership Mentoring</h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Confidential, strategic sounding-board support for CEOs and Registered Managers to navigate leadership pressures.
              </p>
              <Link to="/services" className="text-sm font-bold text-navy-900 flex items-center gap-1 group-hover:text-gold-600 transition-colors">
                Learn More <SafeIcon name="ChevronRight" className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;